import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { useMelwaysLink } from '@seek/melways-react';
import { useEffect } from 'react';
import { useNavigate, useParams, useRouteLoaderData } from 'react-router';

import {
  PageLoader,
  useConfig,
  type Reviews,
  type SortOrder,
} from '@seek/libs-shared';

import { useService } from '../../../';
import { CompanyProfilesPage } from '../../../paths';
import type { AppConfig } from '../../../shared/config';
import { useAuth } from '../../../shared/providers/authProvider';
import type { FlagReviewPayload } from '../../../shared/services/companyProfileService/types';
import { isLocationBasedReviewRoute } from '../../../shared/utils/isLocationBasedReviewRoute';
import { ReviewReplyNotice } from '../../components/reviewReplyNotice/ReviewReplyNotice';
import { SeoStructuredData } from '../../components/seoStructuredData/SeoStructuredData';
import { mapToSeoData } from '../../components/seoStructuredData/mapper';
import { ReviewsPage } from './ReviewsPage';
import { mapCompanyReviewLocationsModel } from './mappers';
import type { ReviewsTabModel } from './reviewsTabModel';

export function ReviewsPageContainer({
  showReviewReplyNotice,
  trackWriteAReviewClicked,
  trackReviewsPaginationClicked,
  trackSortOrderChanged,
  trackSignInClicked,
  trackRegisterClicked,
}: {
  showReviewReplyNotice: boolean;
  trackRegisterClicked: () => void;
  trackReviewsPaginationClicked: ({
    pageNumber,
  }: {
    pageNumber: number;
  }) => void;
  trackSignInClicked: () => void;
  trackSortOrderChanged: ({ sortOrder }: { sortOrder: SortOrder }) => void;
  trackWriteAReviewClicked: () => void;
}) {
  // TODO: This needs investigation to fix
  const model =
    useRouteLoaderData<ReviewsTabModel>(CompanyProfilesPage.Reviews) ??
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useRouteLoaderData<ReviewsTabModel>(CompanyProfilesPage.ReviewsLocation);

  const { companyProfileService, locationService } = useService();
  const { authenticationStatus, ssoLogin } = useAuth();
  const navigate = useNavigate();
  const localize = useMelwaysLink();
  const config = useConfig<AppConfig>();
  const params = useParams();
  const isLocationRoute = isLocationBasedReviewRoute({
    locationSlug: params.locationSlug,
    locationId: config.reviewLocation.details?.locationId,
  });

  const getReviews = async ({
    companyId,
    page,
    sortOrder,
    perPage,
    locationId,
  }: {
    companyId: string;
    page: number;
    sortOrder: SortOrder;
    locationId?: number;
    perPage?: number;
  }): Promise<Reviews> => {
    const resp = await companyProfileService.getReviews({
      companyId,
      page,
      sortOrder,
      perPage,
      locationId,
    });
    return resp;
  };

  const getCompanyReviewLocations = async (variables: {
    companyId: string;
    sort?: boolean;
  }) =>
    mapCompanyReviewLocationsModel(
      await locationService.getCompanyReviewLocations(variables),
    );

  const upvoteReview = async ({
    reviewId,
    companyId,
  }: {
    companyId: string;
    reviewId: string;
  }): Promise<void> => {
    if (
      authenticationStatus === AUTHENTICATION_TYPES.UNAUTHENTICATED &&
      typeof ssoLogin === 'function'
    ) {
      void ssoLogin();
    }
    await companyProfileService.postUpvoteReview({
      companyId,
      reviewId,
    });
  };

  const flagReview = async (
    companyId: string,
    reviewId: string,
    payload: FlagReviewPayload,
  ): Promise<boolean> => {
    if (
      authenticationStatus === AUTHENTICATION_TYPES.UNAUTHENTICATED &&
      typeof ssoLogin === 'function'
    ) {
      void ssoLogin();
    }

    const response = await companyProfileService.postFlagReview({
      companyId,
      reviewId,
      details: payload.details,
      reason: payload.reason,
    });
    return response?.success || false;
  };

  useEffect(() => {
    if (isLocationRoute && !model?.reviews?.reviews?.length) {
      /**
       ** Navigate to all-company-reviews page if there are no location-based reviews.
       */
      void navigate(
        localize({
          language: config.language,
          path: `/companies/${params.companySlug}/reviews${window.location.search}${window.location.hash}`,
        }),
      );
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationStatus, companyProfileService, model]);

  if (showReviewReplyNotice) {
    return <ReviewReplyNotice />;
  }

  /**
   ** disable indexing ONLY for location-based reviews page if:
   **  1. There are no reviews.
   **  2. OR Location language is different from site language.
   */
  const disableIndexing =
    isLocationRoute &&
    (!model?.reviews?.reviews?.length ||
      !config.reviewLocation.matchesSiteLanguage);

  return model ? (
    <>
      <SeoStructuredData
        disableIndexing={disableIndexing}
        data={mapToSeoData(model)}
      />
      <ReviewsPage
        model={model}
        showReviewReplyNotice={showReviewReplyNotice}
        getReviews={getReviews}
        getCompanyReviewLocations={getCompanyReviewLocations}
        upvoteReview={upvoteReview}
        flagReview={flagReview}
        trackWriteAReviewClicked={trackWriteAReviewClicked}
        trackReviewsPaginationClicked={trackReviewsPaginationClicked}
        trackSortOrderChanged={trackSortOrderChanged}
        trackSignInClicked={trackSignInClicked}
        trackRegisterClicked={trackRegisterClicked}
        isLocationRoute={isLocationRoute}
      />
    </>
  ) : (
    <PageLoader />
  );
}
